import { useState } from 'react'
import { useInView } from '../../hooks/useInView'
import { GOALS, reachGoal } from '../../lib/metrika'
import { ReminderModal } from '../ReminderModal'
import phonesTop1 from '../../assets/how-it-works/phones-top-1.png'
import phonesTop2 from '../../assets/how-it-works/phones-top-2.png'
import phonesTop3 from '../../assets/how-it-works/phones-top-3.png'
import phonesTop4 from '../../assets/how-it-works/phones-top-4.png'
import phoneSelect from '../../assets/how-it-works/phone-select.png'
import phoneContacts from '../../assets/how-it-works/phone-contacts.png'
import phoneGift from '../../assets/how-it-works/phone-gift.png'

const CARD_BG = {
  background: 'linear-gradient(180deg, #f3f9ff 0%, #a6edfa 100%)',
  border: '1px solid #d3d5d8',
}

const BOTTOM_CARDS = [
  {
    title: 'Выбор подарка',
    description: (
      <>
        Пользователь переходит на&nbsp;витрину{' '}
        <a
          href="https://ris.promo/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => reachGoal(GOALS.showcaseClick, { place: 'секция как работает' })}
          className="underline decoration-from-font"
        >
          RIS.Promo
        </a>{' '}
        и&nbsp;выбирает подходящее предложение.
      </>
    ),
    phone: phoneSelect,
    phoneAlt: 'Экран выбора подарка',
  },
  {
    title: 'Сбор контактов',
    description: 'Пользователь оставляет телефон или email, и его контакт моментально появляется в вашем личном кабинете.',
    phone: phoneContacts,
    phoneAlt: 'Экран сбора контактов',
  },
  {
    title: 'Получение подарка',
    description: 'Когда пользователь забрал подарок, он получит сообщение с инструкцией.',
    phone: phoneGift,
    phoneAlt: 'Экран получения подарка',
  },
]

function fadeStyle(inView: boolean, delay: number) {
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(28px)',
    transition: `opacity 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms, transform 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms`,
  }
}

export function HowItWorks() {
  const { ref, inView } = useInView(0.8, '0px 0px -80px 0px')
  const [reminderOpen, setReminderOpen] = useState(false)
  return (
    <section
      id="how-it-works"
      className="mx-auto flex max-w-[1920px] flex-col items-center gap-8 px-4 py-14 sm:gap-[42px] sm:px-6 sm:py-20 lg:px-[7%] lg:py-28"
    >
      {/* Header */}
      <div ref={ref} className="flex flex-col items-center gap-5 text-center" style={fadeStyle(inView, 0)}>
        <h2 className="font-display text-[28px] max-[359px]:text-[24px] font-semibold leading-[1.14] text-[#041527] sm:text-[44px] lg:text-[56px] lg:leading-[64px]">
          Как&nbsp;клиенты находят вас?
        </h2>
        <p className="font-body max-w-[748px] text-[15px] leading-[23px] text-[#676b94] sm:text-[16px] sm:leading-[26px] lg:text-[20px] lg:leading-[30px]">
          Человек делает заказ у&nbsp;крупного бренда и&nbsp;в&nbsp;качестве приятного бонуса получает ваше предложение.
        </p>
      </div>

      {/* Cards */}
      <div className="flex w-full flex-col gap-4">
        {/* Top card — Удачный момент */}
        <div
          className="relative w-full overflow-hidden rounded-[20px]"
          style={{ ...CARD_BG, ...fadeStyle(inView, 100) }}
        >
          <div className="px-7 pt-7 pb-0 lg:px-[27px] lg:pt-[42px]">
            <p className="mb-[11px] text-[18px] font-medium leading-6 tracking-[0.3px] text-[#041527] sm:text-[22px] sm:leading-[28px] lg:text-[24px]">
              Удачный момент
            </p>
            <p className="font-body max-w-[559px] text-[15px] leading-[22px] text-[#394959] sm:text-[16px] sm:leading-6">
              Пользователь оформляет покупку на&nbsp;сайте партнёра и&nbsp;видит сообщение с&nbsp;предложением выбрать подарок.
            </p>
          </div>
          {/* Mobile: один телефон снизу */}
          <div className="mt-auto flex h-[320px] items-end justify-center overflow-hidden pt-4 lg:hidden">
            <img src={phonesTop1} alt="Экран телефона" className="block h-full w-auto object-contain object-bottom" />
          </div>
          {/* Desktop: 4 телефона — делят ширину поровну, не вылезают за карточку */}
          <div className="mt-5 hidden grid-cols-4 items-end gap-4 px-[27px] lg:grid">
            {[phonesTop1, phonesTop2, phonesTop3, phonesTop4].map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Экран телефона ${i + 1}`}
                className="h-auto w-full max-h-[420px] object-contain object-bottom"
              />
            ))}
          </div>
        </div>

        {/* Bottom 3 cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {BOTTOM_CARDS.map((card, i) => (
            <div
              key={card.title}
              className="flex flex-col overflow-hidden rounded-[20px]"
              style={{ ...CARD_BG, ...fadeStyle(inView, 250 + i * 150) }}
            >
              <div className="flex-none px-7 pt-7">
                <p className="mb-[11px] text-[18px] font-medium leading-6 tracking-[0.3px] text-[#041527] sm:text-[22px] sm:leading-[28px] lg:text-[24px]">
                  {card.title}
                </p>
                <p className="font-body text-[15px] leading-[22px] text-[#394959] sm:text-[16px] sm:leading-6">
                  {card.description}
                </p>
              </div>
              <div className="mt-auto flex h-[320px] items-end justify-center overflow-hidden pt-4 lg:h-[380px]">
                <img
                  src={card.phone}
                  alt={card.phoneAlt}
                  className="block h-full w-auto object-contain object-bottom"
                  style={{ imageRendering: 'auto' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA: основная кнопка, под ней серая текстовая «напомнить позже» */}
      <div
        className="flex flex-col items-center gap-3"
        style={fadeStyle(inView, 700)}
      >
        <a
          href="https://lk.ris.promo/auth/register"
          onClick={() => reachGoal(GOALS.signupClick, { place: 'секция как работает' })}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-[16px] font-medium text-[#fcfcfc] transition duration-300 ease-out hover:brightness-110 active:brightness-90"
          style={{ background: 'linear-gradient(180deg, #0d6fe0 0%, #145aff 100%)' }}
        >
          Создать аккаунт
        </a>

        <button
          type="button"
          onClick={() => {
            reachGoal(GOALS.reminderOpen, { place: 'секция как работает' })
            setReminderOpen(true)
          }}
          className="inline-flex items-center justify-center text-[15px] font-medium text-[#8a8f9e] transition-colors duration-200 hover:text-[#5c6376]"
        >
          Напомнить позже
        </button>
      </div>

      {reminderOpen && <ReminderModal onClose={() => setReminderOpen(false)} />}
    </section>
  )
}
